import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import Actions from 'actions/Actions';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import Modal from 'components/Modal';
import { Box, Flex } from 'components/ui';
import {
    colors, space, mediaQueries, borders, radii
} from 'style/config';
import ProductCardZone from 'components/Catalog/ProductGrid/CompareModal/Zones/ProductCardZone';
import ColorsZone from 'components/Catalog/ProductGrid/CompareModal/Zones/ColorsZone';
import SizesZone from 'components/Catalog/ProductGrid/CompareModal/Zones/SizesZone';
import RatingsAndReviewsZone from 'components/Catalog/ProductGrid/CompareModal/Zones/RatingsAndReviewsZone';
import OverviewZone from 'components/Catalog/ProductGrid/CompareModal/Zones/OverviewZone';
import IngredientsZone from 'components/Catalog/ProductGrid/CompareModal/Zones/IngredientsZone';
import PickupAndDeliveryZone from 'components/Catalog/ProductGrid/CompareModal/Zones/PickupAndDeliveryZone';
import useScrollSync from 'components/Catalog/ProductGrid/hooks/useScrollSync';
import useScrollThreshold from 'components/Catalog/ProductGrid/hooks/useScrollThreshold';
import useScrollChainPrevention from 'components/Catalog/ProductGrid/hooks/useScrollChainPrevention';
import useScrollForwarding from 'components/Catalog/ProductGrid/hooks/useScrollForwarding';
import useComparisonFetch from 'components/Catalog/ProductGrid/hooks/useComparisonFetch';
import useComparisonData from 'components/Catalog/ProductGrid/hooks/useComparisonData';
import { useComparisonSkus, validateSkuAndParent } from 'components/Catalog/ProductGrid/hooks/useComparisonSkus';
import useComparisonAvailability from 'components/Catalog/ProductGrid/hooks/useComparisonAvailability';
import Storage from 'utils/localStorage/Storage';
import Constants from 'utils/localStorage/Constants';

const { COMPARE_MODAL_SCROLL_POSITION_KEY } = Constants;

const CompareModal = ({
    isOpen, onDismiss, getText = () => {}, selectedProducts = [], handleProductRemove = () => {}
}) => {
    const dispatch = useDispatch();
    const { comparisonData, comparisonError } = useComparisonData();
    const skus = useComparisonSkus(comparisonData);
    const availabilities = useComparisonAvailability(comparisonData);
    const hasShownApiFailureRef = useRef(false);
    // Scroll synchronization between sticky header and content
    const { stickyRef, contentRef, syncFromSticky, syncFromContent } = useScrollSync();

    // Track scroll position for compact mode
    const { isScrolled, handleScroll: handleThresholdScroll, scrollRef } = useScrollThreshold(1, 173, 0);

    // Prevent scroll chaining to body
    useScrollChainPrevention(scrollRef, isOpen);

    // Forward vertical scroll from sticky ProductCardZone to main content area
    useScrollForwarding(stickyRef, contentRef, isOpen);

    // Fetch comparison data
    useComparisonFetch(isOpen, selectedProducts, onDismiss);

    const saveScrollPosition = useCallback(() => {
        if (contentRef.current) {
            Storage.local.setItem(COMPARE_MODAL_SCROLL_POSITION_KEY, contentRef.current.scrollTop);
        }
    }, [contentRef]);

    useEffect(() => {
        if (!isOpen) {
            Storage.local.removeItem(COMPARE_MODAL_SCROLL_POSITION_KEY);
        }
    }, [isOpen]);

    useEffect(() => {
        const savedScrollPosition = Storage.local.getItem(COMPARE_MODAL_SCROLL_POSITION_KEY);

        if (savedScrollPosition == null || comparisonData == null || !contentRef.current) {
            return;
        }

        const scrollTop = Number(savedScrollPosition);

        if (Number.isFinite(scrollTop)) {
            contentRef.current.scrollTop = scrollTop;
        }

        Storage.local.removeItem(COMPARE_MODAL_SCROLL_POSITION_KEY);
    }, [comparisonData, contentRef]);

    useEffect(() => {
        if (isOpen) {
            const previousPageName =
                digitalData?.page?.attributes?.sephoraPageInfo?.pageName || digitalData?.page?.attributes?.previousPageData?.pageName || '';

            processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                data: {
                    pageName: anaConsts.PAGE_NAMES.COMPARE_PRODUCT_MODAL,
                    pageType: anaConsts.PAGE_TYPES.COMPARE_PRODUCT,
                    pageDetail: anaConsts.PAGE_DETAIL.COMPARE_PRODUCT_MODAL,
                    previousPageName
                }
            });
        }
    }, [isOpen]);

    // Combine scroll handlers for content area
    const handleContentScroll = useCallback(() => {
        syncFromContent();
        handleThresholdScroll();
    }, [syncFromContent, handleThresholdScroll]);

    // Callback ref to assign both contentRef and scrollRef to the same element
    const setContentRefs = useCallback(
        node => {
            contentRef.current = node;
            scrollRef.current = node;
        },
        [contentRef, scrollRef]
    );

    useEffect(() => {
        if (!isOpen) {
            hasShownApiFailureRef.current = false;

            return;
        }

        if (comparisonData === null && comparisonError === null) {
            return;
        }

        const expectedProductCount = selectedProducts?.length || 0;

        if (expectedProductCount < 2) {
            // nothing to compare with, we need at least 2 products to compare
            return;
        }

        const validatedSkuCount = skus.reduce((count, sku) => count + (validateSkuAndParent(sku) ? 1 : 0), 0);

        // When the user attempts to Compare 2 products but API only receives 1 product
        // When a user attempts to compare 3 products but the API returns 2 Product skus
        const shouldTriggerApiFailure = !!comparisonError || validatedSkuCount < expectedProductCount;

        if (!shouldTriggerApiFailure || hasShownApiFailureRef.current) {
            return;
        }

        hasShownApiFailureRef.current = true;

        // 1) Dismiss the Comparison Matrix modal
        // 2) Show the "Unable To Compare Products" popup (global InfoModal)
        onDismiss();

        dispatch(
            Actions.showInfoModal({
                isOpen: true,
                title: getText('unableToCompareProductsTitle'),
                message: getText('unableToCompareProductsMessage'),
                buttonText: getText('gotIt'),
                showCloseButton: true
            })
        );
    }, [comparisonData, comparisonError, dispatch, getText, isOpen, onDismiss, selectedProducts, skus]);

    if (!isOpen) {
        return null;
    }

    const customStyle = {
        inner: {
            width: '100%',
            height: '100%',
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column',
            [mediaQueries.sm]: {
                width: '80%',
                height: '90%',
                borderRadius: radii[2]
            }
        }
    };

    const showColorsZone = skus?.some(sku => !!sku?.color);
    // const showOverviewZone = skus?.some(sku => ['Makeup', 'Skincare', 'Hair', 'Fragrance'].includes(sku?.rootParentCategory));
    const showOverviewZone = true;

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={onDismiss}
            width='80%'
            showDismiss={true}
            hasBodyScroll={true}
            margin='auto'
            customStyle={customStyle}
            dataAt='compare_modal'
        >
            {/* Sticky Header */}
            <Modal.Header baseCss={styles.stickyHeader}>
                <Flex css={styles.headerContent}>
                    <Box css={styles.headerSpacer} />
                    <Box css={styles.headerTitleContainer}>
                        <Modal.Title>{getText('compareProducts')}</Modal.Title>
                    </Box>
                    <Box css={styles.headerSpacer} />
                </Flex>
            </Modal.Header>

            {/* Scrollable Body with Horizontal Scrolling */}
            <Modal.Body style={styles.bodyContainer}>
                {/* Sticky Product Card Zone - FER-208 */}
                <ProductCardZone
                    comparisonData={comparisonData}
                    handleProductRemove={handleProductRemove}
                    getText={getText}
                    scrollRef={stickyRef}
                    onScroll={syncFromSticky}
                    isCompact={isScrolled}
                />

                <Box
                    ref={setContentRefs}
                    css={styles.scrollableContainer}
                    onScroll={handleContentScroll}
                    data-at={Sephora.debug.dataAt('compare_modal_scrollable_container')}
                >
                    {/* Colors Zone - FER-245 */}
                    {showColorsZone && (
                        <ColorsZone
                            skus={skus}
                            getText={getText}
                        />
                    )}

                    {/* Sizes Zone - FER-248 */}
                    <SizesZone
                        skus={skus}
                        getText={getText}
                    />

                    {/* Ratings and Reviews Zone - FER-252, FER-258 */}
                    <RatingsAndReviewsZone
                        skus={skus}
                        getText={getText}
                    />

                    {/* Overview Zone - FER-357 */}
                    {showOverviewZone && (
                        <OverviewZone
                            skus={skus}
                            getText={getText}
                        />
                    )}

                    {/* Ingredients Zone - Jira TBD */}
                    <IngredientsZone
                        skus={skus}
                        getText={getText}
                    />

                    {/* Pickup & Delivery Zone - Jira TBD */}
                    <PickupAndDeliveryZone
                        skus={skus}
                        availabilities={availabilities}
                        getText={getText}
                        saveScrollPosition={saveScrollPosition}
                    />
                </Box>
            </Modal.Body>
        </Modal>
    );
};

const styles = {
    stickyHeader: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: colors.white,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: `${space[4]}px`,
        paddingBottom: `${space[4]}px`,
        paddingLeft: `${space[4]}px`,
        // Add extra right padding to account for X button area (X button is absolutely positioned)
        // X button: 13px icon + padding on both sides
        paddingRight: `${space[4] + 13 + space[4]}px`,
        borderBottom: `${borders[1]} ${colors.lightGray}`,
        [mediaQueries.sm]: {
            paddingRight: `${space[5] + 13 + space[5]}px`,
            paddingLeft: `${space[5]}px`,
            borderTopRightRadius: radii[2],
            borderTopLeftRadius: radii[2]
        }
    },
    headerContent: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        position: 'relative'
    },
    headerSpacer: {
        width: '40px',
        flexShrink: 0,
        // Match the width of the X button area to balance the layout
        // X button is 13px icon + padding (space[4] = 16px on each side)
        [mediaQueries.sm]: {
            width: '48px'
        }
    },
    headerTitleContainer: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center'
    },
    bodyContainer: {
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        paddingLeft: `${space[4]}px`,
        paddingTop: 0
    },
    scrollableContainer: {
        flex: 1,
        overflow: 'auto',
        overflowX: 'scroll',
        paddingRight: `${space[4]}px`,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none'
        },
        // On mobile, allow horizontal scrolling (show 1-2 products at a time).
        // proximity (not mandatory) prevents vertical-scroll drift from triggering a snap jump.
        [mediaQueries.xsMax]: {
            scrollSnapType: 'x proximity'
        },
        [mediaQueries.sm]: {
            paddingX: `${space[4]}px`,
            scrollSnapType: 'x proximity',
            overflow: 'scroll'
        }
    }
};

CompareModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onDismiss: PropTypes.func.isRequired,
    getText: PropTypes.func.isRequired,
    selectedProducts: PropTypes.arrayOf(PropTypes.object),
    handleProductRemove: PropTypes.func
};

export default wrapFunctionalComponent(CompareModal, 'CompareModal');
